import { type FilterBarFilterConfig, type FilterState, type FilterValue } from './contracts';
import { FilterCondition } from 'framework/types';

export const isActiveFilterValue = (value: FilterValue) =>
  Array.isArray(value) ? value.length > 0 : value !== null && value !== '';

export const getFilterValue = (value: FilterState, filter: FilterBarFilterConfig): FilterValue => {
  if (filter.type === 'empty') return filter.value || null;
  return filter.type === 'dateRange'
    ? getDateRangeValue(value, filter.source)
    : (value[filter.source]?.[filter.condition] ?? null);
};

export const hasActiveFilters = (value: FilterState, filters: FilterBarFilterConfig[]) =>
  filters.some(filter => isActiveFilterValue(getFilterValue(value, filter)));

export const getDateRangeValue = (state: FilterState, source: string): FilterValue => {
  const conditions = state[source];
  const from = typeof conditions?.ge === 'string' ? conditions.ge : '';
  const to = typeof conditions?.le === 'string' ? conditions.le : '';
  return from || to ? [from, to] : null;
};

export const formatDateRange = (value: FilterValue): string => {
  const [from, to] = Array.isArray(value) ? value : [];
  if (from && to) return `${from} – ${to}`;
  if (from) return `From ${from}`;
  if (to) return `To ${to}`;
  return '';
};

export const setDateRangeValue = (state: FilterState, source: string, value: FilterValue): FilterState => {
  const [from, to] = Array.isArray(value) ? value : [];
  const nextFilters = { ...state };
  const conditions = { ...nextFilters[source] };
  if (from) conditions.ge = from;
  else delete conditions.ge;
  if (to) conditions.le = to;
  else delete conditions.le;

  if (Object.keys(conditions).length) nextFilters[source] = conditions;
  else delete nextFilters[source];
  return nextFilters;
};

export const setFilterValue = (state: FilterState, filter: FilterBarFilterConfig, value: FilterValue): FilterState => {
  if (filter.type === 'empty') return state;
  if (filter.type === 'dateRange') return setDateRangeValue(state, filter.source, value);

  const normalizedValue = filter.condition === FilterCondition.IN && typeof value === 'string' ? [value] : value;
  const nextFilters = { ...state };
  const conditions = { ...nextFilters[filter.source] };

  if (isActiveFilterValue(normalizedValue)) conditions[filter.condition] = normalizedValue;
  else delete conditions[filter.condition];

  if (Object.keys(conditions).length) nextFilters[filter.source] = conditions;
  else delete nextFilters[filter.source];
  return nextFilters;
};
